import { getLocaleText } from "shared/boot/i18n";
import { assetUrl } from "shared/helpers/assets";
import { invertRecord } from "shared/helpers/record";

export * from "./breakpoints";
export * from "./currencies";
export * from "./industries";
export * from "./languages";
export * from "./locations";
export * from "./organisation";
export * from "./outreach";
export * from "./outreach-ai";
export * from "./regexp";
export * from "./reports";
export * from "./social";

export const debug = Boolean(import.meta.env.VITE_DEBUG);
export const defaultTimezone = import.meta.env.VITE_TIME_ZONE;
export const isAdminMode = Boolean(import.meta.env.VITE_ADMIN_MODE);
export const v1BaseUrl = `${import.meta.env.VITE_API_BASE_URL}/v1`;
export const identityBaseUrl = String(import.meta.env.VITE_IDENTITY_BASE_URL);
export const adminBaseUrl = `${import.meta.env.VITE_API_BASE_URL}/admin`;
export const publicBaseUrl = `${import.meta.env.VITE_API_BASE_URL}/public`;
export const directUploadUrl = `${
  import.meta.env.VITE_API_BASE_URL
}/rails/active_storage/direct_uploads`;
export const blobRedirectUrl = "/rails/active_storage/blobs/redirect";
export const stockAliases = { "^AORD": "XAO" };
export const streamTypes = {
  bookmarkStream: 1,
  mentionStream: 0,
  organisationBrief: 4,
  reportingStream: 2,
  socialStream: 3,
};

export function getAccessLevels() {
  return {
    read_only: {
      label: getLocaleText("global.view"),
      permissions: ["read_only"],
    },
    read_write: {
      label: getLocaleText("global.edit"),
      permissions: ["read_write", "read_only"],
    },
  };
}

export const mentionStreamTypes = [
  streamTypes.mentionStream,
  streamTypes.organisationBrief,
];
export const brandProfileTypes = {
  outreach: "OrganisationBrandOutreach",
  reporting: "OrganisationBrandReporting",
};

export const CDN_ICON_PATH = assetUrl("icons/");

export const weekdays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
export const shortWeekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const tagPalette = {
  "#5cdbd3": { fontColor: "white" },
  "#69c0ff": { fontColor: "white" },
  "#70d687": { fontColor: "white" },
  "#85a5ff": { fontColor: "white" },
  "#b5f5ec": { fontColor: "#08979c" },
  "#b37feb": { fontColor: "white" },
  "#bae7ff": { fontColor: "#1890ff" },
  "#c3f7cf": { fontColor: "#4b9f5d" },
  "#d6e4ff": { fontColor: "#597ef7" },
  "#efdbff": { fontColor: "#9254de" },
  "#ff85c0": { fontColor: "white" },
  "#ff7875": { fontColor: "white" },
  "#ff9143": { fontColor: "white" },
  "#ffccc7": { fontColor: "#ff4d4f" },
  "#ffd6e7": { fontColor: "#f759ab" },
  "#ffe0bd": { fontColor: "#fa6f19" },
};

export const defaultBookmarkPalette = {
  "var(--s-color-denim-9)": { fontColor: "#FFFFFF" },
};

export const defaultTagColor = "#70d687";

export const DAY_IN_MILLISECONDS = 86400000;
export const DAY_IN_SECONDS = 86400;
export const DAY_IN_MINUTES = 1440;
export const DAYS_PER_WEEK = 7;
export const MINUTE_IN_MILLISECONDS = 60000;
export const MINUTE_IN_SECONDS = 60;
export const HOUR_IN_MILLISECONDS = 3600000;
export const HOUR_IN_SECONDS = 3600;
export const HOUR_IN_MINUTES = 60;
export const HALF_A_SECOND = 500;

export const MILLISECONDS = {
  approxMonth: 2592000000,
  day: DAY_IN_MILLISECONDS,
  hour: HOUR_IN_MILLISECONDS,
  millisecond: 1,
  minute: MINUTE_IN_MILLISECONDS,
  second: 1000,
  week: 604800000,
} as const;

export const NEXT_CLIP_OFFSET = 61;

export const MAX_ITEM_REQUEST_DURATION_HOURS = 1.5;

// Stream Expiry
export const STREAM_EXTENDED_EXPIRY_DAYS = 3;
export const streamExpiryOptions = [
  { label: "Never", value: 0 },
  { label: "24 hours", value: 1 },
  { label: "3 days", value: 3 },
  { label: "7 days", value: 7 },
  { label: "2 weeks", value: 14 },
  { label: "1 month", value: 28 },
];

export const POPOUT_PLAYER_TARGET = "player-popout-target";

export const derRoles = [
  { class: "bg-radio", label: "Drafter", value: 1 },
  { class: "bg-online", label: "Proofreader", value: 2 },
  { class: "bg-tv", label: "Approver", value: 3 },
];

export const reportTypesDisplayKeypath = {
  insights: "constants.index.insights",
  mentions: "constants.index.mentions",
  social_analyse: "constants.index.social_analyse",
  social_mentions: "constants.index.social_mentions",
};

export const streamHydrationStates = {
  completed: 0,
  failed: 3,
  requested: 1,
  running: 2,
  triggered: 4,
};

export const SOCIAL_ENGAGE_POST_STATES = {
  draft: "0",
  error: "5",
  live: "3",
  partially_sent: "8",
  pending_approval: "1",
  queued: "4",
  rejected: "7",
  scheduled: "2",
} as const;

export const SOCIAL_ENGAGE_POST_STATE_LOOKUP = invertRecord(
  SOCIAL_ENGAGE_POST_STATES
);

export const SOCIAL_ENGAGE_POST_EVENT_LOOKUP = {
  1: "created",
  2: "edited",
  3: "approved",
  4: "post published",
  5: "scheduled",
  6: "approval requested",
  7: "unpublish requested",
  8: "post removed",
  9: "delete requested",
  10: "deleted",
  11: "unpublish approved",
  12: "returned to draft",
  13: "rejected",
  14: "post failed",
  15: "retried",
} as const;

export const SOCIAL_ENGAGE_POST_EVENT_NAMES = invertRecord(
  SOCIAL_ENGAGE_POST_EVENT_LOOKUP
);

export const SOCIAL_ENGAGE_CHARACTER_LIMITS = {
  facebook: 63206,
  instagram: 2200,
  linkedin: 3000,
  twitter: 280,
};

export const socialAutoRefreshEnabled = true;

export const defaultMentionsFields =
  "source,program_airing,excerpts,keywords,word_count,audience,thumbnails,syndications,related_article,entities_sentiment";

// The percentage of the chart that is cut out of the middle.
export const chartCutOuts = {
  doughnut: 50,
  pie: 0,
  slimDoughnut: 78,
};

export const organisationContractTypes = [
  {
    id: 1,
    label: "SLA",
  },
  {
    id: 2,
    label: "Additional Daily Email Report",
  },
  {
    id: 3,
    label: "Additional Transcripts or Instant Edit Transcripts",
  },
  {
    id: 4,
    label: "Additional Media Item Downloads or Instant Edit Downloads",
  },
  {
    id: 5,
    label: "Additional Print Allowance",
  },
  {
    id: 6,
    label: "Additional Users",
  },
  {
    id: 7,
    label: "Additional Media Analysis Reporting",
  },
  {
    id: 8,
    label: "Additional Scope (e.g. NZ Content, InternationalPlus, CALD)",
  },
  {
    id: 9,
    label: "Additional Social Monitoring (Influencer)",
  },
  {
    id: 10,
    label: "Additional Social Monitoring (Engage only)",
  },
  {
    id: 11,
    label: "Additional Social Monitoring (Monitoring, Analysis and Engage)",
  },
  {
    id: 12,
    label: "Additional Keywords or Clients (if PR Agency)",
  },
  {
    id: 13,
    label: "Additional Service (e.g Outreach, CRM)",
  },
];

export const contractTypes = {
  additionalDailyEmailReport: 2,
  additionalKeywordsOrClients: 12,
  additionalMediaAnalysisReporting: 7,
  additionalMediaItemDownloads: 4,
  additionalPrintAllowance: 5,
  additionalScope: 8,
  additionalSocialMonitoring: 11,
  additionalSocialMonitoringEngageOnly: 10,
  additionalSocialMonitoringInfluencer: 9,
  additionalTranscripts: 3,
  additionalUsers: 6,
  SLA: 1,
};

export const organisationContractTiers = [
  { label: "Tier 1 - more than $8,000 per month (excl GST)", value: 1 },
  { label: "Tier 2 - $3,750 to $8,000 per month (excl GST)", value: 2 },
  { label: "Tier 3 - $1,750 to $3,750 per month (excl GST)", value: 3 },
  { label: "Tier 4 - up to $1,750 per month (excl GST)", value: 4 },
  { label: "Project / Analysis", value: 5 },
];

export const UPSELL_LOCKED_FOR_DAYS = 4 * 7; // 4 weeks;

export const upsellRegistrationContractServices = {
  cald: "CALD",
  crm: "CRM",
  der: "DER",
  influencer_social: "Influencer Social",
  insights: "Media Analysis & Insights",
  insights_plus: "Insights Plus",
  instant_edit_downloads: "Instant Edit Downloads",
  instant_edit_transcripts: "Instant Edit Transcripts",
  international_plus: "InternationalPlus",
  magazines: "Magazines",
  media_item_downloads: "Media Item Downloads",
  new_zealand_online_content: "New Zealand Online Content",
  new_zealand_print_content: "New Zealand Print Content",
  outreach: "Outreach",
  podcasts: "Podcasts",
  print: "Print Items",
  search_available_content: "Search Available Content",
  social_engage: "Streem Social - Engage",
  social_listen: "Streem Social - Listen/Analyse",
  streaming_links: "Streaming Links",
  transcripts: "Transcripts",
};

export const FeatureToUpsellDashboardFeature = {
  audio_transcript: "Transcripts",
  has_cald_sources: "CALD",
  has_instant_edit_media_item: "Instant Edit Downloads",
  has_instant_edit_transcript: "Instant Edit Transcripts",
  has_international_plus: "InternationalPlus",
  has_journalist_enquiries: "CRM",
  has_organisation_report_broadcast_preview_links: "Streaming Links",
  has_outreach_activities_lists_search: "Outreach Lite",
  has_outreach_media_releases: "Outreach",
  has_search_available_content: "Search Available Content",
  has_social_engage: "Social Engage",
  has_social_platform: "Social Listen/Analyse",
  media_request: "Media Items",
  podcast_mentions: "Podcasts",
  social_mentions: "Influencer Social",
  view_magazine_articles: "Magazines",
  view_new_zealand_online_content: "NZ Online",
  view_new_zealand_print_and_magazine_content: "NZ Print and Magazine",
  view_print_articles: "Print",
};

export const FeaturesMappedToServices = {
  audio_transcript: "transcripts",
  has_cald_sources: "cald",
  has_instant_edit_media_item: "instant_edit_downloads",
  has_instant_edit_transcript: "instant_edit_transcripts",
  has_international_plus: "international_plus",
  has_journalist_enquiries: "crm",
  has_organisation_report_broadcast_preview_links: "streaming_links",
  has_search_available_content: "search_available_content",
  has_social_engage: "social_engage",
  has_social_platform: "social_listen",
  media_request: "media_item_downloads",
  podcast_mentions: "podcasts",
  social_mentions: "influencer_social",
  view_magazine_articles: "magazines",
  view_new_zealand_online_content: "new_zealand_online_content",
  view_new_zealand_print_and_magazine_content:
    "new_zealand_print_and_magazine_content",
  view_print_articles: "print",
};

export const reasonsToArchiveAcompany = [
  "Poorly Qualified",
  "Ceased Monitoring",
  "Budget Cut",
  "Product",
  "BD/Account Service",
  "Short Term Deal",
  "Invoicing Dispute",
  "Unknown",
  "COVID-19",
  "Machinery of Government Change",
  "Lack of Social",
  "Key Contact MMO Preference",
  "Full Service Upgrade",
  "Licencing Change",
  "Merger/Acquisition",
];

export const competitors = [
  "Meltwater",
  "Isentia",
  "Streem",
  "Unknown",
  "Ceased Monitoring",
  "N/A",
];

export const ONE_MB_IN_BYTES = 1048576;

export const audienceDataSuppliers = [
  "oztam",
  "nielsen",
  "regional_tam",
  "regional_tam_aggregation",
];

export const websiteScrapers = ["SimpleScraper", "DiffbotScraper"];

export const audienceDataTypesRequireSchedule = ["regional_tam"];

const transcriptRequestStaleErrorMessage =
  "This Transcript Request has already been updated by another user so your changes cannot be saved. " +
  "Please refresh this page or open this Transcript Request in another browser window and copy your changes over.";

const tvMediaRequestStaleErrorMessage =
  "This TV Media Request has already been updated by another user so your changes cannot be saved. " +
  "Please refresh this page or open this TV Media Request in another browser window and copy your changes over.";

const radioMediaRequestStaleErrorMessage =
  "This Radio Media Request has already been updated by another user so your changes cannot be saved. " +
  "Please refresh this page or open this Radio Media Request in another browser window and copy your changes over.";

export const STALE_ERROR_MESSAGE = {
  radioMediaRequest: radioMediaRequestStaleErrorMessage,
  transcriptRequest: transcriptRequestStaleErrorMessage,
  tvMediaRequest: tvMediaRequestStaleErrorMessage,
};

// query snippets regex:
// valid snippets should be {qs-:id-:suffix}
// where :suffix is only present to help users identify the snippet
export const querySnippetRegex = /{qs-\d+(-\S*)?}/g;

export const invoiceStatuses = {
  approved: "Approved",
  defer: "Deferred",
  draft: "Draft",
  error: "Failed to create in Xero",
  hold: "On Hold",
  manual: "Manually raised",
  pending: "Pending creation in Xero",
  write_off: "Invoice written off",
  xero: "Invoice created in Xero",
};

export const invoiceTypes = {
  contract: "Contract",
  usage: "Usage",
};

export const maxCardTitleLength = 65;

export const notificationDeliveryMethods = {
  email: "Email",
  microsoftTeams: "Teams",
  mobile: "Mobile",
  slack: "Slack",
};

export const notificationTypes = {
  spike: "StreamNotificationSpike",
  standard: "StreamNotificationStandard",
};

export const spikeNotificationTypes = {
  negativeSentiment: "negative_sentiment",
  positiveSentiment: "positive_sentiment",
  socialShares: "social_shares",
  volume: "volume",
} as const;

export type SpikeType =
  (typeof spikeNotificationTypes)[keyof typeof spikeNotificationTypes];

export const maxBriefsPerClient = 3;

export const insightsReportTypes = [
  {
    label: "Quantitative",
    value: "quantitative",
  },
  {
    label: "Qualitative",
    value: "qualitative",
  },
  {
    label: "Insights Plus",
    value: "insights_plus",
  },
];

export const insightsEntityTypes = [
  {
    label: "Sentiment",
    value: "Sentiment",
  },
  {
    label: "Highlight",
    value: "Highlight",
  },
  {
    label: "Spokesperson",
    value: "Spokesperson",
  },
];

export const insightsTabsByReportType = {
  insights_plus: [
    { label: "Reports", name: "reports" },
    { label: "Queries", name: "queries" },
    { label: "Report Groups", name: "groups" },
  ],
  qualitative: [
    { label: "Reports", name: "reports" },
    { label: "Queries", name: "queries" },
    { label: "Topics", name: "topics" },
    { label: "Key Messages", name: "key_messages" },
    { label: "Entities", name: "entities" },
    { label: "Report Groups", name: "groups" },
  ],
  quantitative: [
    { label: "Reports", name: "reports" },
    { label: "Queries", name: "queries" },
    { label: "Topics", name: "topics" },
    { label: "Report Groups", name: "groups" },
  ],
};

export const insightsGlobalGroupsTabsByReportType = {
  qualitative: [
    { label: "Topics", name: "topics" },
    { label: "Key Messages", name: "key_messages" },
    { label: "Entities", name: "entities" },
  ],
  quantitative: [{ label: "Topics", name: "topics" }],
};

export const insightsSentimentOptions = [
  { label: "40", value: 40 },
  { label: "45", value: 45 },
  { label: "50", value: 50 },
  { label: "55", value: 55 },
  { label: "60", value: 60 },
];

export const insightsInfluenceOptions = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
];

export const instantInsightsPlusPendingStatuses = [
  "indexing_pending",
  "indexing_in_progress",
  "indexing_completed",
  "building_pending",
  "building_in_progress",
  "html_in_progress",
  "pdf_in_progress",
];

export const insightsQueryProcessingStatuses = [
  "export_pending",
  "export_in_progress",
  "sentiment_rating_pending",
  "sentiment_rating_in_progress",
];

export const instantInsightsDoubleWidthChartSortTooltip =
  "Double width charts are pinned to the top of your dashboard.";

export const scrapeFailureTypes = {
  "DiffbotScraperService::DiffbotApiError": "Diffbot failed to scrape",
  "ScrapeOnlineArticle:SimpleScraperError": "NewsScraper failed to scrape",
  "ScrapeOnlineArticle:TitleOrBodyBlank":
    "NewsScraper returned no title and/or body",
};

export const tvProgramCategoryRelevanceSettings = [
  {
    label: "Relevant",
    value: true,
  },
  {
    label: "Not Relevant",
    value: false,
  },
];

export const billingCycles = [
  {
    id: 0,
    label: "Monthly in advance",
    value: "MTH-ADV",
  },
  {
    id: 1,
    label: "Monthly in arrears",
    value: "MTH-ARR",
  },
  {
    id: 2,
    label: "Quarterly in advance",
    value: "QRT-ADV",
  },
  {
    id: 3,
    label: "Quarterly in arrears",
    value: "QRT-ARR",
  },
  {
    id: 4,
    label: "Halfyearly in advance",
    value: "HLF-ADV",
  },
  {
    id: 5,
    label: "Halfyearly in arrears",
    value: "HLF-ARR",
  },
  {
    id: 6,
    label: "Annual in advance",
    value: "ANN-ADV",
  },
  {
    id: 7,
    label: "Short term - unspecified",
    value: "PROJECT",
  },
];

export const mrrServiceGroups = {
  additional_clients_keywords: "mediaMonitoring",
  additional_users: "mediaMonitoring",
  ave: "mediaMonitoring",
  cald: "mediaMonitoring",
  ccp: "mediaMonitoring",
  cla: "mediaMonitoring",
  crm: "mediaMonitoring",
  der: "mediaMonitoring",
  financial_times: "mediaMonitoring",
  influencer_social: "mediaMonitoring",
  insights: "mediaAnalysisAndInsights",
  insights_plus: "mediaAnalysisAndInsights",
  instant_edit_downloads: "mediaMonitoring",
  instant_edit_transcripts: "mediaMonitoring",
  international_lite: "mediaMonitoring",
  international_max: "mediaMonitoring",
  international_plus: "mediaMonitoring",
  lexisnexis: "mediaMonitoring",
  magazines: "mediaMonitoring",
  media_item_downloads: "mediaMonitoring",
  new_zealand_content: "mediaMonitoring",
  new_zealand_online_content: "mediaMonitoring",
  new_zealand_print_content: "mediaMonitoring",
  nla: "mediaMonitoring",
  outreach: "mediaMonitoring",
  podcasts: "mediaMonitoring",
  print: "mediaMonitoring",
  radio_tv_broadcasts: "mediaMonitoring",
  risk_score: "mediaMonitoring",
  search_available_content: "mediaMonitoring",
  sky_news: "mediaMonitoring",
  social_engage: "streemSocial",
  social_listen: "streemSocial",
  traditional: "mediaMonitoring",
  transcripts: "mediaMonitoring",
  tveyes: "mediaMonitoring",
};

export const broadcastMedia = [
  {
    id: "tv",
    label: "TV",
  },
  {
    id: "radio",
    label: "Radio",
  },
];

export const ORGANISATION_REPORT_SPEC_PRODUCTS = {
  afr_pdf: "AFR PDF",
  australia_back_page_summaries: "Australia Back Page Summaries",
  australia_front_page_summaries: "Australia Front Page Summaries",
  automatic_summary: "Automatic Summary",
  coverage_summaries: "Coverage Summaries",
  front_and_back_page_summaries: "Front/Back Page Summaries",
  front_pages_pdf: "Front Pages PDF",
  in_focus: "In Focus",
  leading_news_table: "Leading News Table",
  new_zealand_back_page_summaries: "New Zealand Back Page Summaries",
  new_zealand_front_page_summaries: "New Zealand Front Page Summaries",
  renewal: "Renewal",
  report_pdf: "Report PDF",
  stock_prices: "Stock Prices",
  twitter_table: "Twitter Table",
};

export const salutationOptions = [
  { label: "Mr.", value: "Mr." },
  { label: "Mrs.", value: "Mrs." },
  { label: "Miss", value: "Miss" },
  { label: "Dr.", value: "Dr." },
  { label: "Ms.", value: "Ms." },
  { label: "Prof.", value: "Prof." },
  { label: "Rev.", value: "Rev." },
  { label: "Herr", value: "Herr" },
  { label: "Frau", value: "Frau" },
];

export const contentTeaserTiles = [
  {
    features: ["social_mentions"],
    id: "influencer_social",
  },
  {
    features: ["podcast_mentions"],
    id: "podcast",
  },
  {
    features: ["view_magazine_articles"],
    id: "magazine",
  },
  {
    features: ["has_international_max", "has_international_plus"],
    id: "international",
  },
  {
    features: ["has_cald_sources"],
    id: "cald",
  },
  {
    features: [
      "view_new_zealand_online_content",
      "view_new_zealand_print_and_magazine_content",
    ],
    id: "nz_content",
  },
];

export const organisationServiceLevels = [
  {
    id: "tier_one_plus",
    label: "Tier 1+",
    region: ["au", "nz"],
  },
  {
    id: "standard_account_management_tier_one",
    label: "Standard Account Management - Tier 1",
    region: ["au", "nz"],
  },
  {
    id: "standard_account_management_tier_two",
    label: "Standard Account Management - Tier 2",
    region: ["au", "nz"],
  },
  {
    id: "standard_account_management_tier_three",
    label: "Standard Account Management - Tier 3",
    region: ["au", "nz"],
  },
  {
    id: "customer_success_tier_three",
    label: "Customer Success - Tier 3",
    region: ["au", "nz"],
  },
  {
    id: "customer_success_tier_four",
    label: "Customer Success - Tier 4",
    region: ["au", "nz"],
  },
  {
    id: "customer_success_tier_five",
    label: "Customer Success - Tier 5",
    region: ["au", "nz"],
  },
  {
    id: "tier_one",
    label: "Tier 1",
    region: ["uk", "us"],
  },
  {
    id: "tier_two",
    label: "Tier 2",
    region: ["uk", "us"],
  },
];

export const ORGANISATION_SOCIAL_HELP_CENTER_CONTACT_OPTIONS = [
  {
    id: "without_contact",
    label: "Standard - Without Contact Information",
    locale: "en-GB",
  },
];

export const ORGANISATION_SOCIAL_SUPPORT_CONTACT_OPTIONS = [
  {
    id: "customer_success",
    label: "Social - Customer Success Manager",
    locale: "en-AU",
  },
  {
    id: "standard",
    label: "Standard",
    locale: "en-GB",
  },
  {
    id: "account_manager",
    label: "Standard - Account Manager",
    locale: "en-GB",
  },
];

export const organisationFeatureBatches = [
  { label: "1 - Outreach", value: 1 },
  { label: "2 - Monitoring Essentials", value: 2 },
  { label: "3 - Monitoring Plus (Essentials + Social + Print)", value: 3 },
  { label: "4 - Outreach + Monitoring Essentials", value: 4 },
  { label: "5 - CisionOne Full Comms Suite", value: 5 },
];

export const organisationContentPackages = [
  {
    label: "Global Online News Monitoring - English only",
    value: "UK-C1-MON-STAN",
  },
  {
    label: "Print Monitoring UK Print Clips 50 per month",
    value: "UK-C1-MON-UKPRINT-50CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 100 per month",
    value: "UK-C1-MON-UKPRINT-100CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 250 per month",
    value: "UK-C1-MON-UKPRINT-250CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 500 per month",
    value: "UK-C1-MON-UKPRINT-500CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 750 per month",
    value: "UK-C1-MON-UKPRINT-750CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 1000 per month",
    value: "UK-C1-MON-UKPRINT-1000CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 1500 per month",
    value: "UK-C1-MON-UKPRINT-1500CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 2000 per month",
    value: "UK-C1-MON-UKPRINT-2000CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 3000 per month",
    value: "UK-C1-MON-UKPRINT-3000CLIPS",
  },
  {
    label: "Print Monitoring UK Print Clips 4000 per month",
    value: "UK-C1-MON-UKPRINT-4000CLIPS",
  },
  {
    label: "Global Premium Monitoring",
    value: "UK-C1-MON-PREMIUM",
  },
  {
    label: "Podcast Monitoring",
    value: "UK-C1-MON-GLBPODCAST",
  },
  {
    label: "Global Broadcast - US + Can",
    value: "UK-C1-MON-USCAN-BROADCAST",
  },
  {
    label: "Global Broadcast - Aus + Nz",
    value: "UK-C1-MON-AUSNZ-BROADCAST",
  },
  {
    label: "UK Broadcast Monitoring",
    value: "UK-C1-MON-UKBROADCAST",
  },
  {
    label: "EMEA Broadcast Monitoring",
    value: "UK-C1-MON-EMEABROADCAST",
  },
  {
    label: "Global Broadcast (ROW)",
    value: "UK-C1-MON-GLOB-BROADCAST",
  },
];

export const regions = [
  { label: "AU", name: "Australia", value: "au" },
  { label: "NZ", name: "New Zealand", value: "nz" },
  { label: "UK", name: "United Kingdom", value: "uk" },
  { label: "US", name: "United States of America", value: "us" },
  { label: "CA", name: "Canada", value: "ca" },
  { label: "DACH", name: "DACH", value: "dach" },
  { label: "NORDIC", name: "Nordics", value: "nordic" },
  { label: "APAC", name: "Asia-Pacific", value: "apac" },
  { label: "FR", name: "France", value: "fr" },
];

export const languages = [
  {
    label: "English",
    value: "en",
  },
  {
    label: "English (Australia)",
    value: "en-AU",
  },
  {
    label: "English (Canada)",
    value: "en-CA",
  },
  {
    label: "English (United Kingdom)",
    value: "en-GB",
  },
  {
    label: "English (United States)",
    value: "en-US",
  },
  {
    label: "Français",
    value: "fr",
  },
  {
    label: "Français (Canada)",
    value: "fr-CA",
  },
  {
    label: "Deutsch",
    value: "de",
  },
  {
    label: "简体中文",
    value: "zh-CN",
  },
];

export const CISION_SOURCE_FEATURES = [
  "has_international_max",
  "has_lexisnexis_print",
  "has_lexisnexis_online",
  "has_lexisnexis_academic",
  "has_wise_nla",
  "has_wise_public",
  "has_french_wise_public",
  "has_wise_cla",
  "has_wise_bonnier",
  "has_nla_eclips_web",
  "has_financial_times_online",
  "has_mediatrack_nla_regional",
  "has_uk_clipped_content_nla",
  "has_uk_clipped_content_cla",
  "has_uk_clipped_content_other",
  "has_english_language_international_nla",
  "has_english_language_international_cla",
  "has_english_language_international_other",
  "has_nla_eclips_print",
  "has_nla_eclips_print_financial_times",
  "has_tveyes_broadcast_anz",
  "has_tveyes_broadcast_us_canada",
  "has_tveyes_broadcast_uk",
  "has_tveyes_broadcast_europe",
  "has_tveyes_broadcast_rest_of_world",
  "has_new_york_times_content",
  "has_dow_jones_content",
  "has_globe_and_mail_content",
  "has_factiva_content",
  "has_mediatrack_apac",
  "has_mediatrack_international",
  "has_cedrom_consumer_current_affairs_and_trade_magazines",
  "has_cedrom_comprehensive_print_and_digital_news",
  "has_cedrom_eastern_canada_print_and_digital_news",
  "has_cedrom_western_canada_print_and_digital_news",
];

export const HIGH_REACT_SCORE_THRESHOLD = 75;

export const NEWS_SUMMARY_TYPES = [
  {
    label: "Australia - Front Pages",
    region: "au",
    value: "australia_front_pages",
  },
  {
    label: "Australia - Back Pages",
    region: "au",
    value: "australia_back_pages",
  },
  {
    label: "New Zealand - Front Pages",
    region: "nz",
    value: "new_zealand_front_pages",
  },
  {
    label: "New Zealand - Back Pages",
    region: "nz",
    value: "new_zealand_back_pages",
  },
];

export const TRANSCRIPTION_ENGINES = [
  { name: "AssemblyAI", value: "assemblyai" },
  { name: "Gladia (Maori)", value: "gladia" },
];

// Strict RFC-4122 pattern (hex groups 8-4-4-4-12) — case-insensitive
export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
